import { Button, Card, Divider, Input, Select, Space, Switch, Typography } from 'antd';
import Title from 'antd/es/typography/Title';
import { v4 as uuidv4 } from 'uuid';
import TipTap from '../../Sections/TipTap';
import { activeTabMap } from '../../constants';

interface RiskAreaProps {
  data: any;
  setData: (path: string, value: any) => void;
  activeTab: string;
  readonly?: boolean;
}

const RiskArea: React.FC<RiskAreaProps> = ({ data, setData, activeTab, readonly = false }) => {
  const impactLevels = [
    { value: 'none', label: 'None' },
    { value: 'low', label: 'Low' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
    { value: 'variable', label: 'Variable' }
  ];

  const risks = [
    { value: 'none', label: 'None' },
    { value: 'low', label: 'Low' },
    { value: 'low_to_medium', label: 'Low to Medium' },
    { value: 'medium', label: 'Medium' },
    { value: 'medium_to_high', label: 'Medium to High' },
    { value: 'high', label: 'High' }
  ];

  const approachTypes = [
    { value: 'none', label: 'No indicator' },
    { value: 'check', label: "Green checkmark (good approach, doesn't create a risk)" },
    { value: 'warning', label: 'Yellow warning (not ideal, but doesn’t create too much risk)' },
    { value: 'cancel', label: 'Red x (this is a big problem)' }
  ];

  const updateRiskAreaData = (key: string, value: any) => {
    if (readonly) return; // Prevent changes in readonly mode
    setData(`methodologies.${activeTab}.${key}`, value);
  };

  const riskAreaData = data[activeTab];
  console.log(activeTab);
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Title level={3}>{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Risk Area</Title>
      <Space>
        <Typography.Text strong>Show this tab on the platform:</Typography.Text>
        <Switch
          checked={riskAreaData.showTab !== false}
          onChange={(checked) => updateRiskAreaData('showTab', checked)}
          disabled={readonly}
        />
      </Space>
      <Title level={5}>Citation list (enter as a numbered list)</Title>
      <TipTap
        value={riskAreaData.citations}
        onChange={(value) => updateRiskAreaData('citations', value)}
        readonly={readonly}
      />
      <Card style={{ marginBottom: '16px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Text strong>Summary</Typography.Text>
          <TipTap
            value={riskAreaData.summary}
            onChange={(value) => updateRiskAreaData('summary', value)}
            readonly={readonly}
          />
          <Space style={{ marginTop: '16px' }}>
            <Typography.Text strong>Show Graph:</Typography.Text>
            <Switch
              checked={riskAreaData.showGraph}
              onChange={(checked) => updateRiskAreaData('showGraph', checked)}
              disabled={readonly}
            />
          </Space>
          {riskAreaData.showGraph && (
            <Space direction="vertical" style={{ width: '100%', marginTop: '16px' }}>
              <Typography.Text strong>Graph Title:</Typography.Text>
              <Input
                placeholder="Graph Title"
                value={riskAreaData.graphTitle}
                onChange={(e) => updateRiskAreaData('graphTitle', e.target.value)}
                disabled={readonly}
              />
              <Typography.Text strong>
                Graph Title Tooltip Text (Optional, if tooltip needed at the end of title):
              </Typography.Text>
              <Input.TextArea
                placeholder="Tooltip text"
                value={riskAreaData.graphTitleTooltipText}
                onChange={(e) => updateRiskAreaData('graphTitleTooltipText', e.target.value)}
                disabled={readonly}
              />
              <Typography.Text strong>Graph Takeaway:</Typography.Text>
              <Input.TextArea
                placeholder="Graph Takeaway"
                value={riskAreaData.graphDescription}
                onChange={(e) => updateRiskAreaData('graphDescription', e.target.value)}
                disabled={readonly}
                showCount
                maxLength={180}
              />
            </Space>
          )}
          <Divider />
          <Space direction="vertical" style={{ width: '100%', marginTop: '16px' }}>
            <Typography.Text strong>
              Impact of {activeTabMap[activeTab].name} on GHG integrity:
            </Typography.Text>
            <Select
              style={{ width: '100%' }}
              mode="multiple"
              value={riskAreaData.ghgImpact}
              onChange={(value) => updateRiskAreaData('ghgImpact', value)}
              options={impactLevels}
              disabled={readonly}
            />
            <Typography.Text strong>Rationale:</Typography.Text>
            <Input.TextArea
              placeholder="GHG Impact Takeaway"
              value={riskAreaData.ghgImpactDescription}
              onChange={(e) => updateRiskAreaData('ghgImpactDescription', e.target.value)}
              disabled={readonly}
              showCount
              maxLength={400}
            />
          </Space>
          <Divider />
          <Space direction="vertical" style={{ width: '100%', marginTop: '16px' }}>
            <Typography.Text strong>
              Impact of methodology selection on {activeTabMap[activeTab].name} risk:
            </Typography.Text>
            <Select
              style={{ width: '100%' }}
              mode="multiple"
              value={riskAreaData.methodologyImpact}
              onChange={(value) => updateRiskAreaData('methodologyImpact', value)}
              options={impactLevels}
              disabled={readonly}
            />
            <Typography.Text strong>Rationale:</Typography.Text>
            <Input.TextArea
              placeholder="Methodology Impact Takeaway"
              value={riskAreaData.methodologyImpactDescription}
              onChange={(e) => updateRiskAreaData('methodologyImpactDescription', e.target.value)}
              disabled={readonly}
              showCount
              maxLength={400}
            />
          </Space>
        </Space>
      </Card>

      <Card>
        {riskAreaData.data.map((item: any, itemIndex: number) => (
          <Space key={item.id} direction="vertical" style={{ width: '100%', marginBottom: '24px' }}>
            <Space
              direction="horizontal"
              style={{ justifyContent: 'space-between', width: '100%' }}>
              <Typography.Title level={5}>Methodology #{itemIndex + 1}</Typography.Title>
              {riskAreaData.data.length > 1 && (
                <Button
                  type="primary"
                  danger
                  onClick={() => {
                    if (readonly) return;
                    const updatedData = riskAreaData.data.filter((d: any) => d.id !== item.id);
                    updateRiskAreaData('data', updatedData);
                  }}
                  disabled={readonly}>
                  Remove
                </Button>
              )}
            </Space>

            <Space direction="vertical" style={{ width: '100%' }}>
              <Input
                placeholder="Standard"
                value={item.standard}
                onChange={(e) => {
                  if (readonly) return;
                  const updatedData = riskAreaData.data.map((d: any) => {
                    if (d.id === item.id) {
                      return { ...d, standard: e.target.value };
                    }
                    return d;
                  });
                  updateRiskAreaData('data', updatedData);
                }}
                style={{ marginBottom: '12px' }}
                disabled={readonly}
              />

              <Input
                placeholder="Methodology"
                value={item.methodology}
                onChange={(e) => {
                  if (readonly) return;
                  const updatedData = riskAreaData.data.map((d: any) => {
                    if (d.id === item.id) {
                      return { ...d, methodology: e.target.value };
                    }
                    return d;
                  });
                  updateRiskAreaData('data', updatedData);
                }}
                style={{ marginBottom: '12px' }}
                disabled={readonly}
              />

              <Input
                placeholder="Version"
                value={item.version}
                onChange={(e) => {
                  if (readonly) return;
                  const updatedData = riskAreaData.data.map((d: any) => {
                    if (d.id === item.id) {
                      return { ...d, version: e.target.value };
                    }
                    return d;
                  });
                  updateRiskAreaData('data', updatedData);
                }}
                style={{ marginBottom: '16px' }}
                disabled={readonly}
              />

              <Card type="inner" title="Approach" style={{ marginBottom: '16px' }}>
                {item.approach.map((approach: any, approachIndex: number) => (
                  <Space
                    key={approach.id}
                    direction="vertical"
                    style={{ width: '100%', marginBottom: '12px' }}>
                    <Space
                      direction="horizontal"
                      style={{ justifyContent: 'space-between', width: '100%' }}>
                      <Typography.Text strong>
                        Approach indicator #{approachIndex + 1}
                      </Typography.Text>
                      {item.approach.length > 1 && (
                        <Button
                          danger
                          size="small"
                          onClick={() => {
                            if (readonly) return;
                            const updatedApproaches = item.approach.filter(
                              (a: any) => a.id !== approach.id
                            );
                            const updatedData = riskAreaData.data.map((d: any) => {
                              if (d.id === item.id) {
                                return { ...d, approach: [...updatedApproaches] };
                              }
                              return d;
                            });
                            updateRiskAreaData('data', updatedData);
                          }}
                          disabled={readonly}>
                          Remove
                        </Button>
                      )}
                    </Space>

                    <Select
                      style={{ width: '100%' }}
                      value={approach.type}
                      placeholder="Select approach type"
                      onChange={(value) => {
                        if (readonly) return;
                        const updatedApproaches = item.approach.map((a: any) => {
                          if (a.id === approach.id) {
                            return { ...a, type: value };
                          }
                          return a;
                        });
                        const updatedData = riskAreaData.data.map((d: any) => {
                          if (d.id === item.id) {
                            return { ...d, approach: [...updatedApproaches] };
                          }
                          return d;
                        });
                        updateRiskAreaData('data', updatedData);
                      }}
                      options={approachTypes}
                      disabled={readonly}
                    />

                    <TipTap
                      value={approach.data}
                      onChange={(value) => {
                        if (readonly) return;
                        const updatedApproaches = item.approach.map((a: any) => {
                          if (a.id === approach.id) {
                            return { ...a, data: value };
                          }
                          return a;
                        });
                        const updatedData = riskAreaData.data.map((d: any) => {
                          if (d.id === item.id) {
                            return { ...d, approach: [...updatedApproaches] };
                          }
                          return d;
                        });
                        updateRiskAreaData('data', updatedData);
                      }}
                      readonly={readonly}
                    />
                  </Space>
                ))}

                <Button
                  type="dashed"
                  style={{ width: '100%' }}
                  onClick={() => {
                    if (readonly) return;
                    const newApproach = {
                      id: uuidv4(),
                      type: 'none',
                      data: null
                    };
                    const updatedData = riskAreaData.data.map((d: any) => {
                      if (d.id === item.id) {
                        return {
                          ...d,
                          approach: [...d.approach, newApproach]
                        };
                      }
                      return d;
                    });
                    updateRiskAreaData('data', updatedData);
                  }}
                  disabled={readonly}>
                  + Add approach indicator
                </Button>
              </Card>

              <Card type="inner" title="Risk" style={{ marginBottom: '16px' }}>
                {item.risk.impact.map((impact: any) => (
                  <Space
                    key={impact.id}
                    direction="horizontal"
                    style={{ marginBottom: '12px', width: '100%' }}>
                    <Select
                      style={{ width: '150px' }}
                      value={impact.impact}
                      onChange={(value) => {
                        if (readonly) return;
                        const updatedImpacts = item.risk.impact.map((i: any, idx: number) => {
                          if (i.id === impact.id) {
                            return { ...i, impact: value };
                          }
                          return i;
                        });
                        const updatedData = riskAreaData.data.map((d: any) => {
                          if (d.id === item.id) {
                            return {
                              ...d,
                              risk: {
                                ...d.risk,
                                impact: updatedImpacts
                              }
                            };
                          }
                          return d;
                        });
                        updateRiskAreaData('data', updatedData);
                      }}
                      options={risks}
                      disabled={readonly}
                    />

                    <Input
                      placeholder="Qualifying text (optional) E.g. for flaring"
                      style={{ flex: 1 }}
                      value={impact.description}
                      onChange={(e) => {
                        if (readonly) return;
                        const updatedImpacts = item.risk.impact.map((i: any) => {
                          if (i.id === impact.id) {
                            return { ...i, description: e.target.value };
                          }
                          return i;
                        });
                        const updatedData = riskAreaData.data.map((d: any) => {
                          if (d.id === item.id) {
                            return {
                              ...d,
                              risk: {
                                ...d.risk,
                                impact: updatedImpacts
                              }
                            };
                          }
                          return d;
                        });
                        updateRiskAreaData('data', updatedData);
                      }}
                      disabled={readonly}
                    />

                    {item.risk.impact.length > 1 && (
                      <Button
                        danger
                        size="small"
                        onClick={() => {
                          if (readonly) return;
                          const updatedImpacts = item.risk.impact.filter(
                            (i: any) => i.id !== impact.id
                          );
                          const updatedData = riskAreaData.data.map((d: any) => {
                            if (d.id === item.id) {
                              return {
                                ...d,
                                risk: {
                                  ...d.risk,
                                  impact: updatedImpacts
                                }
                              };
                            }
                            return d;
                          });
                          updateRiskAreaData('data', updatedData);
                        }}
                        disabled={readonly}>
                        Remove
                      </Button>
                    )}
                  </Space>
                ))}

                <Button
                  type="dashed"
                  style={{ width: '100%', marginBottom: '12px' }}
                  onClick={() => {
                    if (readonly) return;
                    const newImpact = { id: uuidv4(), impact: 'low', description: '' };
                    const updatedData = riskAreaData.data.map((d: any) => {
                      if (d.id === item.id) {
                        return {
                          ...d,
                          risk: {
                            ...d.risk,
                            impact: [...d.risk.impact, newImpact]
                          }
                        };
                      }
                      return d;
                    });
                    updateRiskAreaData('data', updatedData);
                  }}
                  disabled={readonly}>
                  + Add impact level (optional, for cases where risk varies)
                </Button>

                <Typography.Text strong>Risk Details</Typography.Text>
                <TipTap
                  value={item.risk.data}
                  onChange={(value) => {
                    if (readonly) return;
                    const updatedData = riskAreaData.data.map((d: any) => {
                      if (d.id === item.id) {
                        return {
                          ...d,
                          risk: {
                            ...d.risk,
                            data: value
                          }
                        };
                      }
                      return d;
                    });
                    updateRiskAreaData('data', updatedData);
                  }}
                  readonly={readonly}
                />
              </Card>

              <Card type="inner" title="Transparency Risk" style={{ marginBottom: '16px' }}>
                {(Array.isArray(item?.transparencyRisk?.impact)
                  ? item?.transparencyRisk?.impact
                  : [item?.transparencyRisk?.impact]
                ).map((impact: any) => (
                  <Space
                    key={impact.id}
                    direction="horizontal"
                    style={{ marginBottom: '12px', width: '100%' }}>
                    <Select
                      style={{ width: '150px' }}
                      value={impact.impact}
                      onChange={(value) => {
                        if (readonly) return;
                        const updatedImpacts = (
                          Array.isArray(item?.transparencyRisk?.impact)
                            ? item?.transparencyRisk?.impact
                            : [item?.transparencyRisk?.impact]
                        ).map((i: any, idx: number) => {
                          if (i.id === impact.id) {
                            if (!Array.isArray(item?.transparencyRisk?.impact)) {
                              return { impact: value, id: impact.id || uuidv4(), description: '' };
                            } else {
                              return { ...i, impact: value };
                            }
                          }
                          return i;
                        });
                        const updatedData = riskAreaData.data.map((d: any) => {
                          if (d.id === item.id) {
                            return {
                              ...d,
                              transparencyRisk: {
                                ...d.transparencyRisk,
                                impact: updatedImpacts
                              }
                            };
                          }
                          return d;
                        });
                        updateRiskAreaData('data', updatedData);
                      }}
                      options={risks}
                      disabled={readonly}
                    />

                    <Input
                      placeholder="Qualifying text (optional) E.g. for flaring"
                      style={{ flex: 1 }}
                      value={impact.description}
                      onChange={(e) => {
                        if (readonly) return;
                        const updatedImpacts = (
                          Array.isArray(item?.transparencyRisk?.impact)
                            ? item?.transparencyRisk?.impact
                            : [item?.transparencyRisk?.impact]
                        ).map((i: any) => {
                          if (i.id === impact.id) {
                            if (!Array.isArray(item?.transparencyRisk?.impact)) {
                              return {
                                impact: i.impact,
                                id: impact.id || uuidv4(),
                                description: e.target.value
                              };
                            } else {
                              return { ...i, description: e.target.value };
                            }
                          }
                          return i;
                        });
                        const updatedData = riskAreaData.data.map((d: any) => {
                          if (d.id === item.id) {
                            return {
                              ...d,
                              transparencyRisk: {
                                ...d.transparencyRisk,
                                impact: updatedImpacts
                              }
                            };
                          }
                          return d;
                        });
                        updateRiskAreaData('data', updatedData);
                      }}
                      disabled={readonly}
                    />

                    {item.transparencyRisk.impact.length > 1 && (
                      <Button
                        danger
                        size="small"
                        onClick={() => {
                          if (readonly) return;
                          const updatedImpacts = item.transparencyRisk.impact.filter(
                            (i: any) => i.id !== impact.id
                          );
                          const updatedData = riskAreaData.data.map((d: any) => {
                            if (d.id === item.id) {
                              return {
                                ...d,
                                transparencyRisk: {
                                  ...d.transparencyRisk,
                                  impact: updatedImpacts
                                }
                              };
                            }
                            return d;
                          });
                          updateRiskAreaData('data', updatedData);
                        }}
                        disabled={readonly}>
                        Remove
                      </Button>
                    )}
                  </Space>
                ))}

                <Button
                  type="dashed"
                  style={{ width: '100%', marginBottom: '12px' }}
                  onClick={() => {
                    if (readonly) return;
                    const newImpact = { id: uuidv4(), impact: 'low', description: '' };
                    const updatedData = riskAreaData.data.map((d: any) => {
                      if (d.id === item.id) {
                        return {
                          ...d,
                          transparencyRisk: {
                            ...d.transparencyRisk,
                            impact: [...d.transparencyRisk.impact, newImpact]
                          }
                        };
                      }
                      return d;
                    });
                    updateRiskAreaData('data', updatedData);
                  }}
                  disabled={readonly}>
                  + Add impact level (optional, for cases where risk varies)
                </Button>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Typography.Text strong>Transparency Details</Typography.Text>
                  <TipTap
                    value={item.transparencyRisk.data}
                    onChange={(value) => {
                      if (readonly) return;
                      const updatedData = riskAreaData.data.map((d: any) => {
                        if (d.id === item.id) {
                          return {
                            ...d,
                            transparencyRisk: {
                              ...d.transparencyRisk,
                              data: value
                            }
                          };
                        }
                        return d;
                      });
                      updateRiskAreaData('data', updatedData);
                    }}
                    readonly={readonly}
                  />
                </Space>
              </Card>

              <Card type="inner" title="Mitigations" style={{ marginBottom: '16px' }}>
                <TipTap
                  value={item.mitigations.data}
                  onChange={(value) => {
                    if (readonly) return;
                    const updatedData = riskAreaData.data.map((d: any) => {
                      if (d.id === item.id) {
                        return {
                          ...d,
                          mitigations: {
                            ...d.mitigations,
                            data: value
                          }
                        };
                      }
                      return d;
                    });
                    updateRiskAreaData('data', updatedData);
                  }}
                  readonly={readonly}
                />
              </Card>
            </Space>
            <Divider />
          </Space>
        ))}
      </Card>
    </Space>
  );
};

export default RiskArea;
